import type { SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import { signalTypeMap } from "app/pages/sales-intelligence/domain/signals/types/signal";
import { getDifferenceDateByDay } from "app/pages/sales-intelligence/sub-modules/intent/helpers/utils";
import type { Topic } from "app/pages/sales-intelligence/sub-modules/intent/types/common";
import { DEFAULT_LOCKED_PERIOD } from "app/pages/sales-intelligence/sub-modules/intent/types/constants";
import { IntentEmptyTopicsCompactBanner } from "app/pages/sales-intelligence/sub-modules/signals/components/shared/IntentEmptyTopicsBanner";
import IntentTopicChangesBadge from "app/pages/sales-intelligence/sub-modules/signals/components/shared/IntentTopicChangesBadge/IntentTopicChangesBadge";
import SignalsComingSoonView from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalsComingSoonView/SignalsComingSoonView";
import type SignalLifecycleState from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalLifecycleState";
import type { SignalTypePresentationConfig } from "app/pages/sales-intelligence/sub-modules/signals/models/view/SignalTypePresentationConfig";
import type UISignalsGroupedByType from "app/pages/sales-intelligence/sub-modules/signals/models/view/UISignalsGroupedByType";

import { deriveSignalTypeBadge } from "../../utils/renderers";
import signalsService from "../signalsService";

export type RenderingContext = {
    readonly signalType: SignalType;
    readonly lifecycleState: SignalLifecycleState | undefined;
    readonly uiSignalsGroupedByType: Readonly<UISignalsGroupedByType>;
    readonly intentTopics: readonly Topic[];
    readonly intentTopicCreatedAt?: Date;
};

const isWithinLockedPeriod = (createdAt: Date): boolean =>
    getDifferenceDateByDay(createdAt, new Date()) < DEFAULT_LOCKED_PERIOD;

const baseConfig = (context: RenderingContext): SignalTypePresentationConfig => ({
    icon: signalsService.getIconForSignalType(context.signalType),
    Badge: deriveSignalTypeBadge(context.lifecycleState),
    CustomViewComponent: null,
});

const resolveIntent = (context: RenderingContext): SignalTypePresentationConfig => {
    const signals = context.uiSignalsGroupedByType[context.signalType] ?? [];
    const showTopicChangesBadge =
        context.intentTopicCreatedAt !== undefined &&
        isWithinLockedPeriod(context.intentTopicCreatedAt) &&
        signals.length > 0;

    return {
        ...baseConfig(context),
        ...(showTopicChangesBadge ? { Badge: IntentTopicChangesBadge } : {}),
        CustomViewComponent: context.intentTopics.length === 0 ? IntentEmptyTopicsCompactBanner : null,
    };
};

const resolveComingSoon = (context: RenderingContext): SignalTypePresentationConfig => ({
    ...baseConfig(context),
    CustomViewComponent: SignalsComingSoonView,
});

export const resolveSignalTypePresentation = (context: RenderingContext): SignalTypePresentationConfig => {
    if (context.signalType === signalTypeMap.intent) {
        return resolveIntent(context);
    }

    if (context.lifecycleState === "coming-soon") {
        return resolveComingSoon(context);
    }

    return baseConfig(context);
};
